// Login health: notice when a login has expired (or never passed the 6-digit-code step) so the app can say
// "please sign in again" instead of pages quietly showing nothing. Since 2026-10-23 the database refuses any
// request whose login didn't pass the authenticator-code step (aal2), and refused reads come back empty.

/** The expiry time (ms) and assurance level of a login's access token, or null if it can't be read. */
export function tokenInfo(token) {
  try {
    const part = String(token || "").split(".")[1];
    const json = JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/")));
    return { expMs: Number(json.exp) * 1000, aal: json.aal || "" };
  } catch { return null; }
}

/**
 * What's wrong with a login, if anything:
 *   "expired"  the access token has run out (or will within `marginMs`) — try renewing it first
 *   "no-code"  the login hasn't passed the authenticator-code step, so the database will refuse it
 *   null       fine
 */
export function loginProblem(session, now = Date.now(), marginMs = 30000) {
  const info = tokenInfo(session?.access_token);
  if (!info) return "expired";
  if (!(info.expMs > now + marginMs)) return "expired";
  if (info.aal !== "aal2") return "no-code";
  return null;
}

/** Is this response a "you're not signed in" refusal from our database or server (not the login screens themselves)? */
export function isLoginRefusal(url, status, supabaseUrl) {
  if (status !== 401) return false;
  const u = String(url || "");
  if (/\/auth\/v1\/(token|logout|verify|recover|otp|signup)/.test(u)) return false;
  if (/\/auth\/v1\/factors\/[^/]+\/(challenge|verify)/.test(u)) return false;
  return (!!supabaseUrl && u.startsWith(supabaseUrl)) || /^\/api\//.test(u) || /^https?:\/\/[^/]+\/api\//.test(u);
}

/** Calls onRefused(url) whenever a request is refused as "not signed in". Returns a function that stops watching. */
export function watchLoginRefusals(supabaseUrl, onRefused) {
  const original = window.fetch;
  const watched = async (...args) => {
    const res = await original(...args);
    const url = typeof args[0] === "string" ? args[0] : args[0]?.url;
    if (isLoginRefusal(url, res.status, supabaseUrl)) { try { onRefused(url); } catch { /* never break the request */ } }
    return res;
  };
  window.fetch = watched;
  return () => { if (window.fetch === watched) window.fetch = original; };
}

/** The words shown when a login needs renewing. */
export const LOGIN_MESSAGES = {
  "expired": "Your login has expired, so nothing can be loaded or saved until you sign in again.",
  "no-code": "Your login hasn't been confirmed with the 6-digit code from your authenticator app, so nothing can be loaded or saved. Please sign in again and enter the code (or set up the authenticator app if you're asked to).",
};
